import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import {
  User,
  Building,
  Phone,
  Instagram,
  Facebook,
  Linkedin,
  MapPin,
  Globe,
  FileText,
  Save,
  Check,
  Settings,
} from 'lucide-react';
import { ImageUpload } from '../components/ImageUpload';
import { BadgeProgressCompact } from '../components/gamification';
// PortraitUpload import kept for when KI-Portraits feature is re-enabled
// import { PortraitUpload } from '../components/PortraitUpload';
import { AGENT_FOCUS_EVENT } from '../ai/context/AIAgentContext';
import type { UIFocus } from '../ai/types/agent';

export function Profile() {
  const { profile, updateProfile } = useAuth();
  const { primaryColor } = useSettings();
  const { i18n } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fullName, setFullName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');

  const [instagramUrl, setInstagramUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');

  const [shortBio, setShortBio] = useState('');
  const [story, setStory] = useState('');
  const [targetAudience, setTargetAudience] = useState('');

  const [productFocus, setProductFocus] = useState('');
  const [consultingStyle, setConsultingStyle] = useState('');

  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('de');

  const [imprintText, setImprintText] = useState('');

  // Focus state for AI Agent highlighting
  const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null);

  // Refs for scrolling to fields
  const fieldRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Listen for AI Agent focus events
  useEffect(() => {
    const handleFocus = (event: Event) => {
      const customEvent = event as CustomEvent<UIFocus>;
      const focus = customEvent.detail;

      if (focus.type === 'profile') {
        setFocusedFieldId(focus.id);

        // Scroll to the focused field after a short delay
        setTimeout(() => {
          const fieldElement = fieldRefs.current.get(focus.id);
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Also focus the input if it's focusable
            if (fieldElement instanceof HTMLInputElement ||
                fieldElement instanceof HTMLTextAreaElement ||
                fieldElement instanceof HTMLSelectElement) {
              fieldElement.focus();
            }
          }
        }, 150);

        // Clear focus highlight after 3 seconds
        setTimeout(() => {
          setFocusedFieldId(null);
        }, 3000);
      }
    };

    window.addEventListener(AGENT_FOCUS_EVENT, handleFocus);
    return () => window.removeEventListener(AGENT_FOCUS_EVENT, handleFocus);
  }, []);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setFullName(profile.full_name || '');
      setProfileImageUrl(profile.profile_image_url || '');
      setPhone(profile.phone || '');
      setCompany(profile.company || '');
      setInstagramUrl(profile.instagram_url || '');
      setTiktokUrl(profile.tiktok_url || '');
      setFacebookUrl(profile.facebook_url || '');
      setLinkedinUrl(profile.linkedin_url || '');
      setShortBio(profile.short_bio || '');
      setStory(profile.story || '');
      setTargetAudience(profile.target_audience || '');
      setProductFocus(profile.product_focus || '');
      setConsultingStyle(profile.consulting_style || '');
      setCity(profile.city || '');
      setCountry(profile.country || '');
      const profileLang = profile.language || 'de';
      setLanguage(profileLang);
      // Sync i18n with stored profile language
      if (['de', 'en'].includes(profileLang)) {
        i18n.changeLanguage(profileLang);
      }
      setImprintText(profile.imprint_text || '');
    }
  }, [profile]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const computedFullName = `${firstName} ${lastName}`.trim() || fullName;

      const { error } = await updateProfile({
        first_name: firstName,
        last_name: lastName,
        full_name: computedFullName,
        profile_image_url: profileImageUrl || undefined,
        phone: phone || undefined,
        company: company || undefined,
        instagram_url: instagramUrl || undefined,
        tiktok_url: tiktokUrl || undefined,
        facebook_url: facebookUrl || undefined,
        linkedin_url: linkedinUrl || undefined,
        short_bio: shortBio || undefined,
        story: story || undefined,
        target_audience: targetAudience || undefined,
        product_focus: productFocus || undefined,
        consulting_style: consultingStyle || undefined,
        city: city || undefined,
        country: country || undefined,
        language: language || 'de',
        imprint_text: imprintText || undefined,
      });

      if (error) throw error;

      // Apply language change immediately
      if (['de', 'en'].includes(language)) {
        i18n.changeLanguage(language);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get focus highlight class
  const getFocusClass = (fieldId: string) =>
    focusedFieldId === fieldId
      ? 'ring-2 ring-primary ring-offset-2 shadow-lg animate-pulse'
      : '';

  // Helper to set field ref
  const setFieldRef = (fieldId: string) => (el: HTMLElement | null) => {
    if (el) {
      fieldRefs.current.set(fieldId, el);
    } else {
      fieldRefs.current.delete(fieldId);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white mb-2">Profil</h1>
          <p className="text-[#6B6B6B] dark:text-slate-400">Verwalte deine persönlichen Informationen und Branding</p>
        </div>
        <Link
          data-tour="settings-button"
          to="/einstellungen"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[#1A1A1A] dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium shadow-sm"
        >
          <Settings className="w-5 h-5" />
          <span>Einstellungen</span>
        </Link>
      </div>

      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-600 dark:text-green-400 text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          Profil erfolgreich aktualisiert
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div data-tour="profile-info" className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start gap-6 pb-6 border-b border-gray-100 dark:border-slate-700">
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profilbild</label>
              <ImageUpload
                value={profileImageUrl}
                onChange={setProfileImageUrl}
                aspectRatio="square"
                placeholder="Bild hochladen"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#1A1A1A] dark:text-white">
                {firstName || lastName ? `${firstName} ${lastName}`.trim() : fullName || 'Benutzer'}
              </h2>
              <p className="text-[#6B6B6B] dark:text-slate-400">{profile?.email}</p>
              {city && country && (
                <p className="text-sm text-[#6B6B6B] dark:text-slate-400 flex items-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {city}, {country}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Persönliche Informationen
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vorname</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-colors"
                  placeholder="Max"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nachname</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-colors"
                  placeholder="Mustermann"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Telefon / WhatsApp</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={setFieldRef('phone') as React.Ref<HTMLInputElement>}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('phone')}`}
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Unternehmen</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-colors"
                    placeholder="Forever Living Products"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div data-tour="profile-social" className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Instagram className="w-5 h-5 text-primary" />
            Social Media
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Instagram</label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={setFieldRef('instagram_url') as React.Ref<HTMLInputElement>}
                  type="url"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('instagram_url')}`}
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">TikTok</label>
              <input
                ref={setFieldRef('tiktok_url') as React.Ref<HTMLInputElement>}
                type="url"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('tiktok_url')}`}
                placeholder="https://tiktok.com/@..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Facebook</label>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={setFieldRef('facebook_url') as React.Ref<HTMLInputElement>}
                  type="url"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('facebook_url')}`}
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">LinkedIn</label>
              <div className="relative">
                <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={setFieldRef('linkedin_url') as React.Ref<HTMLInputElement>}
                  type="url"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('linkedin_url')}`}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Über mich
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Kurze Bio</label>
              <textarea
                ref={setFieldRef('short_bio') as React.Ref<HTMLTextAreaElement>}
                value={shortBio}
                onChange={(e) => setShortBio(e.target.value)}
                rows={3}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all resize-none ${getFocusClass('short_bio')}`}
                placeholder="Kurze Beschreibung für dein Profil..."
              />
              <p className="text-xs text-gray-500 mt-1">Wird auf deinen Landingpages verwendet</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Meine Geschichte</label>
              <textarea
                ref={setFieldRef('story') as React.Ref<HTMLTextAreaElement>}
                value={story}
                onChange={(e) => setStory(e.target.value)}
                rows={5}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all resize-none ${getFocusClass('story')}`}
                placeholder="Erzähle deine persönliche Geschichte..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Zielgruppe</label>
              <input
                ref={setFieldRef('target_audience') as React.Ref<HTMLInputElement>}
                type="text"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('target_audience')}`}
                placeholder="z.B. Mütter, die flexibel von zuhause arbeiten wollen"
              />
            </div>
          </div>
        </div>

        {/* KI-Portraits – temporarily hidden (component kept for later use):
        <PortraitUpload />
        */}

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-primary" />
            Produkte & Beratung
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Produktfokus</label>
              <input
                ref={setFieldRef('product_focus') as React.Ref<HTMLInputElement>}
                type="text"
                value={productFocus}
                onChange={(e) => setProductFocus(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('product_focus')}`}
                placeholder="z.B. Hautpflege, Darmgesundheit"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Beratungsstil</label>
              <input
                ref={setFieldRef('consulting_style') as React.Ref<HTMLInputElement>}
                type="text"
                value={consultingStyle}
                onChange={(e) => setConsultingStyle(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('consulting_style')}`}
                placeholder="z.B. Persönlich, einfühlsam, ganzheitlich"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Standort
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Stadt</label>
              <input
                ref={setFieldRef('city') as React.Ref<HTMLInputElement>}
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('city')}`}
                placeholder="Berlin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Land</label>
              <input
                ref={setFieldRef('country') as React.Ref<HTMLInputElement>}
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${getFocusClass('country')}`}
                placeholder="Deutschland"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Sprache</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  ref={setFieldRef('language') as React.Ref<HTMLSelectElement>}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all bg-white appearance-none ${getFocusClass('language')}`}
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Badge Progress - Compact View */}
        <div data-tour="profile-badges">
          <BadgeProgressCompact />
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Impressum
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Impressum Text</label>
            <textarea
              ref={setFieldRef('imprint_text') as React.Ref<HTMLTextAreaElement>}
              value={imprintText}
              onChange={(e) => setImprintText(e.target.value)}
              rows={6}
              className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all resize-none font-mono text-sm ${getFocusClass('imprint_text')}`}
              placeholder="Max Mustermann&#10;Musterstraße 1&#10;12345 Musterstadt&#10;&#10;E-Mail: beispiel@email.de&#10;Telefon: +49 123 456789"
            />
            <p className="text-xs text-gray-500 mt-1">
              Wird im Footer deiner öffentlichen Seiten angezeigt (rechtlich erforderlich)
            </p>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 text-white px-8 py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: primaryColor }}
          >
            <Save className="w-5 h-5" />
            {loading ? 'Speichert...' : 'Änderungen speichern'}
          </button>
        </div>
      </form>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <h3 className="text-lg font-semibold text-[#1A1A1A] dark:text-white mb-4">Kontoinformationen</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-[#6B6B6B] dark:text-slate-400">E-Mail</span>
            <span className="text-[#1A1A1A] dark:text-white font-medium">{profile?.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-[#6B6B6B] dark:text-slate-400">Konto erstellt</span>
            <span className="text-[#1A1A1A] dark:text-white font-medium">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('de-DE') : '-'}
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-[#6B6B6B] dark:text-slate-400">Zuletzt aktualisiert</span>
            <span className="text-[#1A1A1A] dark:text-white font-medium">
              {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('de-DE') : '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
